// src/components/ai/RainNowPanel.js
// --- UPDATE (weather v2) — NEW FILE -----------------------------------------------------------
// "Rain right now" strip under the AI Weather & Flood Intelligence card (Live Readings).
//   • What FlowSense believes is falling now and WHY: a staff report (who, how long ago) or the weather
//     models (how many of them show rain now — "2 of 6 models" means the forecast is unsure).
//   • Next 6 h: the expected total and the wettest model ("up to").
//   • Staff rain report: Light / Moderate / Heavy / Violent / Stopped (WMO intensity classes). A report
//     overrides the models for the next 1–3 h and refreshes the drainage outlook + road ponding forecast.
// FlowSense has no rain gauge, so a person on the ground is the only measurement — the panel says so.
// --- END UPDATE -------------------------------------------------------------------------------
import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Flex, HStack, Text, Button, Tooltip, Badge, Icon, useColorModeValue, useToast, Wrap, WrapItem,
} from '@chakra-ui/react';
import { MdWaterDrop, MdCloud, MdPersonPinCircle, MdWarningAmber } from 'react-icons/md';
import { RAIN_INTENSITIES } from '../../services/aiWeatherService';

const fmtMm = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `${Number(v) < 10 ? Number(v).toFixed(1) : Math.round(Number(v))} mm`);
const ago = (min) => (min == null ? '' : min < 1 ? 'just now' : min < 60 ? `${min} min ago` : `${Math.round(min / 60)} h ago`);

function RainNowPanel({ weather, weatherStatus, onReport }) {
  const toast = useToast();
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const obs = weather?.observation || null;
  const models = weather?.models || null;
  const cur = weather?.current || {};
  const reported = !!(obs && obs.fresh && obs.intensity !== 'none');
  const split = models && models.agreement === 'split';
  const busy = !!weatherStatus?.reporting;

  const send = useCallback(async (intensity, label) => {
    try {
      await onReport(intensity);
      toast({
        title: intensity === 'none' ? 'Rain stopped — reported' : `${label} rain reported`,
        description: 'The weather card, drainage outlook and road water forecast update within a minute.',
        status: 'success', duration: 5000, isClosable: true, position: 'top-right',
      });
    } catch (e) {
      toast({ title: 'Rain report not saved', description: e?.message || String(e), status: 'error', duration: 7000, isClosable: true, position: 'top-right' });
    }
  }, [onReport, toast]);

  const tone = reported ? 'purple' : split ? 'orange' : (cur.rateMmH || cur.precipitationMm) > 0 ? 'blue' : 'green';

  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderLeftWidth="4px" borderLeftColor={`${tone}.400`} borderRadius="xl"
      px={4} py={3} mt={3} data-testid="rain-now-panel">
      <Flex gap={4} align={{ base: 'stretch', lg: 'center' }} direction={{ base: 'column', lg: 'row' }}>
        <HStack spacing={3} flex="1" minW={0} align="flex-start">
          <Icon as={reported ? MdPersonPinCircle : MdCloud} boxSize={6} color={`${tone}.400`} mt={0.5} />
          <Box minW={0}>
            <HStack spacing={2} wrap="wrap">
              <Text fontWeight="800" fontSize="sm">Rain now: {cur.label || '—'}</Text>
              {reported && <Badge colorScheme="purple" variant="solid" fontSize="2xs">STAFF REPORT</Badge>}
              {!reported && models?.count > 0 && <Badge colorScheme={split ? 'orange' : 'gray'} fontSize="2xs">{models.count} MODELS</Badge>}
              {weather?.stale && <Badge colorScheme="yellow" fontSize="2xs">UPDATING</Badge>}
            </HStack>
            <Text fontSize="xs" color={muted} data-testid="rain-now-why">
              {reported
                ? `${obs.label} reported ${ago(obs.age_min)}${obs.by ? ` by ${obs.by}` : ''}${obs.source === 'tech-app' ? ' (tech app)' : ''}${obs.reports_last_hour > 1 ? ` · ${obs.reports_last_hour} reports in the last hour` : ''} — overrides the models for the next 1–3 h.`
                : models?.count
                  ? `${models.wet_now} of ${models.evaluated_now} weather models show rain this hour${split ? ' — they disagree, showers are likely somewhere' : ''}. No rain gauge: if it is raining where you are, report it.`
                  : 'Single weather model (forecast, not a measurement). If it is raining where you are, report it.'}
            </Text>
            <HStack spacing={4} mt={1} fontSize="xs">
              <Text><b>{fmtMm(weather?.next6hRainMm)}</b> next 6 h{weather?.upper6hRainMm != null && weather.upper6hRainMm > (weather.next6hRainMm || 0) + 0.5 ? <Text as="span" color={muted}> (up to {fmtMm(weather.upper6hRainMm)})</Text> : null}</Text>
              <Text><b>{fmtMm(weather?.past24hRainMm)}</b> last 24 h</Text>
              {weather?.source && <Text color={muted}>{weather.source}</Text>}
            </HStack>
            {Array.isArray(weather?.warnings) && weather.warnings.length > 0 && (
              <HStack spacing={1} mt={1} color="orange.400" fontSize="2xs"><Icon as={MdWarningAmber} /><Text>{weather.warnings[0]}</Text></HStack>
            )}
          </Box>
        </HStack>
        {onReport && (
          <Box>
            <Text fontSize="2xs" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wider" mb={1}>
              <Icon as={MdWaterDrop} mb="-2px" /> Is it raining where you are?
            </Text>
            <Wrap spacing={1}>
              {RAIN_INTENSITIES.map((r) => (
                <WrapItem key={r.key}>
                  <Tooltip hasArrow label={r.key === 'none' ? 'The rain has stopped here' : `${r.label} rain here now (${r.range})`}>
                    <Button size="xs" variant={obs?.fresh && obs.intensity === r.key ? 'solid' : 'outline'} colorScheme={r.colorScheme}
                      isDisabled={busy} onClick={() => send(r.key, r.label)}
                      data-testid={`rain-report-${r.key}`} aria-label={r.key === 'none' ? 'Report that the rain has stopped' : `Report ${r.label.toLowerCase()} rain`}>
                      {r.label}
                    </Button>
                  </Tooltip>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        )}
      </Flex>
    </Box>
  );
}

RainNowPanel.propTypes = {
  weather: PropTypes.object,
  weatherStatus: PropTypes.object,
  onReport: PropTypes.func,
};

export default memo(RainNowPanel);
